import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  inject,
  signal,
  computed,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import Swal from 'sweetalert2';
import { RH_ACTIONS } from '@shared/constants/rh-actions.constants';
import { SesionService } from '@intelishs-group/session-core';
import { EmpleadoService } from '@services/empleado.service';
import { VacacionesService } from '@services/vacaciones.service';
import { FestivosService } from '@services/festivos.service';
import { EmpleadoList } from '@schemas/empleado.schema';
import {
  SolicitudVacaciones,
  SaldoVacaciones,
  EstatusVacaciones,
} from '@schemas/vacaciones.schema';
import { getEstatusColor as _getEstatusColor } from '@shared/vacaciones.helpers';
import { leerSaldo } from '@shared/vacaciones.utils';
import { SolicitudResolucionModal } from '../../components/solicitud-resolucion-modal/solicitud-resolucion-modal';
import { SolicitudEditarModal } from '../../components/solicitud-editar-modal/solicitud-editar-modal';
import { SolicitudNuevaModal } from '../../components/solicitud-nueva-modal/solicitud-nueva-modal';

import {
  CellStack,
  PageHeader,
  TableActions,
  type ActionButtonDef,
  DataTable,
  CellTemplateDirective,
  type ColumnDef,
  type TableAction,
} from '@intelishs-group/ui';

@Component({
  selector: 'app-solicitar-vacaciones',
  imports: [
    DatePipe,
    DecimalPipe,
    FormsModule,
    NzSelectModule,
    NzDatePickerModule,
    NzInputModule,
    NzButtonModule,
    NzIconModule,
    NzModalModule,
    NzTooltipModule,
    NzDividerModule,
    DataTable,
    CellTemplateDirective,
    TableActions,
    CellStack,
    PageHeader,
    SolicitudResolucionModal,
    SolicitudEditarModal,
    SolicitudNuevaModal,
  ],
  templateUrl: './solicitar-vacaciones.page.html',
  styleUrl: './solicitar-vacaciones.page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SolicitarVacacionesPage implements OnInit {
  private readonly empleadoService = inject(EmpleadoService);
  private readonly vacacionesService = inject(VacacionesService);
  readonly festivosService = inject(FestivosService);
  private readonly message = inject(NzMessageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sesion = inject(SesionService);

  empleados = signal<EmpleadoList[]>([]);
  selectedEmpleadoId = signal<number | null>(null);
  saldo = signal<SaldoVacaciones | null>(null);
  solicitudes = signal<SolicitudVacaciones[]>([]);
  loading = signal(false);

  /**
   * Rol del portal SSO (ej. "Administrador RH", "Director RH", "Empleado RH").
   * Se llena desde useSessionService() / SessionFallback en ngOnInit.
   */

  /**
   * True si el usuario logueado puede operar sobre otros empleados (alta
   * administrativa de RH). Admin/RH ven el dropdown completo; el resto (Director,
   * Empleado, o rol no mapeado) se autoselecciona vía /api/Empleado/me.
   */
  /**
   * ID interno del propio usuario, para el subtítulo.
   *
   * Es el dato con el que RH lo identifica y hasta ahora un empleado no podía
   * leerlo en ningún lado: aquí solo aparecía dentro del `<nz-option>` del
   * selector administrativo, que ve Admin/RH, y la bienvenida es su único otro
   * acceso. Se muestra solo cuando la pantalla es "la mía" —con el selector
   * activo el ID que importa es el del empleado elegido, y ese ya sale ahí—.
   */
  readonly miIdInterno = signal<string | null>(null);

  /**
   * ID del empleado SOBRE EL QUE SE ESTÁ TRABAJANDO, no necesariamente el mío.
   *
   * Con el selector administrativo el punto es actuar como el empleado elegido,
   * así que el ID debe seguir a la selección; sin selector, es el propio. Se
   * busca en la lista cargada y, si no está —caso empleado/director, que nunca
   * carga la lista completa—, cae al de `getMe()`.
   */
  readonly subtituloPagina = computed<string>(() => {
    const seleccionado = this.empleados().find((e) => e.id === this.selectedEmpleadoId());
    const id = seleccionado?.idInterno ?? this.miIdInterno();
    const base = 'Consulta tu saldo y envía solicitudes de vacaciones';
    return id ? `${id} · ${base}` : base;
  });

  /**
   * ¿Puede solicitar vacaciones a nombre de OTRO empleado?
   *
   * Antes: `toRolCanonico(rol) === 'Admin' || === 'RH'`, con el rol leído del
   * token. Ahora pregunta por el MODULO otorgado, que es el mismo conjunto de
   * personas —`empleados` lo tiene solo `administrador-rh`, que es a quien
   * mapeaban 'Admin' y 'RH'— pero sin depender de como este escrito el nombre
   * del rol en cada ambiente.
   *
   * ⚠️ Lo PRECISO seria una FUNCION otorgada (`vacaciones.solicitar-por-otro`),
   * porque «administrar empleados» y «pedir vacaciones por otro» no son lo mismo
   * aunque hoy los tenga la misma gente. Se usa el modulo para no inventar datos
   * que nadie ha sembrado, y queda anotado para cuando se definan funciones.
   *
   * El superadministrador pasa sin tenerlo: `tieneModulo` lo contesta por el.
   */
  readonly puedeSeleccionarOtro = computed<boolean>(() => this.sesion.tieneModulo('empleados'));

  /**
   * Vista del empleado sobre sus propias solicitudes, así que no hay columna de
   * empleado. Mismo patrón que vacaciones-lista: estado anclado a la izquierda,
   * acciones ancladas a la derecha y el periodo en una sola columna en vez de
   * tres (inicio + fin + días).
   */
  columns: ColumnDef<SolicitudVacaciones>[] = [
    {
      key: 'estatus',
      header: 'Estatus',
      width: '115px',
      align: 'center',
      type: 'tag',
      tagColorFn: (v) => _getEstatusColor(v),
    },
    // Lo que el empleado viene a ver: cuándo son sus vacaciones.
    // `ellipsis` porque la celda es un `cell-stack`: sus dos lineas no truncan
    // solas, ensanchan la columna.
    {
      key: 'fechaInicio',
      header: 'Periodo',
      width: 'auto',
      type: 'custom',
      ellipsis: true,
      sortable: true,
    },
    {
      key: 'fechaSolicitud',
      header: 'Solicitada',
      width: '115px',
      align: 'center',
      type: 'date',
      sortable: true,
    },
    // Sin columna de Comentarios: el botón "Ver resolución" ya abre el modal
    // donde el empleado lee su propio comentario junto con el resultado. Una
    // columna de texto libre aquí solo competiría por ancho con el periodo.
    // Hasta 3 botones de 24px + 2 gaps de 6px = 84px más el padding de celda.
    {
      key: 'acciones',
      header: 'Acciones',
      width: '100px',
      align: 'center',
      type: 'custom',
    },
  ];

  // Sin extraFilterKeys ni exportColumns a propósito: esta tabla va con
  // [searchable]="false" y sin botón de exportar —son las solicitudes propias
  // del empleado, no un listado que se busque o se trabaje en Excel—, así que
  // ninguno de los dos tendría efecto.

  getActionsForRow(row: SolicitudVacaciones): ActionButtonDef[] {
    return [
      RH_ACTIONS.resolucion,
      ...(row.estatus === 'Pendiente' ? [RH_ACTIONS.editar] : []),
      ...(this.puedeCancelar(row.estatus) ? [RH_ACTIONS.cancelar] : []),
    ];
  }

  onAction(event: TableAction<SolicitudVacaciones>): void {
    switch (event.action) {
      case 'resolucion':
        this.abrirResolucion(event.row);
        break;
      case 'editar':
        this.abrirEditar(event.row);
        break;
      case 'cancelar':
        this.confirmarCancelar(event.row);
        break;
    }
  }

  // Modal nueva solicitud
  nuevaModalVisible = signal(false);

  // Modal resolución (vista del empleado)
  resolVisible = signal(false);
  resolSolicitud = signal<SolicitudVacaciones | null>(null);

  // Modal edición
  editModalVisible = signal(false);
  editSolicitud = signal<SolicitudVacaciones | null>(null);

  /** Periodo anterior: el más antiguo no vencido, no en curso, con días disponibles (FIFO: se consume primero) */
  periodoAnterior = computed(() => {
    const s = this.saldo();
    if (!s) return null;
    return s.periodos.find((p) => !p.enCurso && !p.vencido && p.diasDisponibles > 0) ?? null;
  });

  /**
   * Tarjeta del ciclo anterior: el vigente, le queden días o no.
   *
   * `periodoAnterior` exige días disponibles porque alimenta los avisos de
   * vencimiento, pero la tarjeta no puede depender de eso: si una solicitud
   * pendiente agotó el ciclo viejo, la tarjeta desaparecía y solo quedaba el
   * actual, así que parecía que los días habían salido del ciclo nuevo.
   */
  cicloAnterior = computed(() => {
    const s = this.saldo();
    if (!s) return null;
    return s.periodos.find((p) => !p.enCurso && !p.vencido) ?? null;
  });

  /** Periodo anterior vencido: se muestra en gris cuando ya no hay uno activo */
  periodoAnteriorVencido = computed(() => {
    const s = this.saldo();
    if (!s || this.cicloAnterior()) return null;
    return (
      s.periodos.filter((p) => !p.enCurso && p.vencido).sort((a, b) => b.numero - a.numero)[0] ??
      null
    );
  });

  /** Periodo actual en curso */
  periodoActual = computed(() => {
    const s = this.saldo();
    if (!s) return null;
    return s.periodos.find((p) => p.enCurso) ?? null;
  });

  /** Días naturales hasta el vencimiento del periodo anterior */
  diasHastaVencimiento = computed((): number | null => {
    const p = this.periodoAnterior();
    if (!p) return null;
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    // new Date(...) directo tolera tanto "2026-05-16" (mock) como
    // "2026-05-16T00:00:00" (backend real). Concatenar 'T00:00:00' rompía
    // el segundo formato → Invalid Date → días NaN.
    const vence = new Date(p.fechaVencimiento);
    if (isNaN(vence.getTime())) return null;
    return Math.ceil((vence.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));
  });

  /** Nivel de urgencia del periodo anterior según días restantes */
  urgenciaPorVencer = computed((): 'critico' | 'advertencia' | null => {
    const dias = this.diasHastaVencimiento();
    if (dias === null) return null;
    if (dias <= 10) return 'critico';
    if (dias <= 30) return 'advertencia';
    return null;
  });

  /** Días en espera de aprobación (Pendiente + Aprobada Director) */
  diasEnEspera = computed(() =>
    this.solicitudes()
      .filter((s) => s.estatus === 'Pendiente' || s.estatus === 'Aprobada Director')
      .reduce((sum, s) => sum + s.diasSolicitados, 0),
  );

  /** Días aprobados con fechas futuras (ya confirmados, próximos a tomar) */
  diasAprobadosFuturos = computed(() => {
    const hoy = new Date().toISOString().slice(0, 10);
    return this.solicitudes()
      .filter((s) => s.estatus === 'Aprobada' && s.fechaInicio >= hoy)
      .reduce((sum, s) => sum + s.diasSolicitados, 0);
  });

  /**
   * Solicitudes "en proceso": las que aún no tienen la aprobación final de RH
   * (Pendiente, esperando al Director, o Aprobada Director, esperando a RH).
   *
   * Ya NO bloquean pedir otra: desde el levantamiento de vacaciones simultáneas
   * (2026-10) un empleado puede tener varias a la vez y cada una se resuelve
   * por separado. Lo que limita es el saldo, y eso lo valida el modal de alta.
   */
  solicitudesEnProceso = computed(() =>
    this.solicitudes().filter((s) => s.estatus === 'Pendiente' || s.estatus === 'Aprobada Director'),
  );

  /** "Tienes 2 solicitudes en proceso (8 días): 1 con tu director, 1 con RH". */
  textoEnProceso = computed((): string => {
    const sols = this.solicitudesEnProceso();
    if (sols.length === 0) return '';
    const conDirector = sols.filter((s) => s.estatus === 'Pendiente').length;
    const conRH = sols.length - conDirector;
    const dias = sols.reduce((sum, s) => sum + s.diasSolicitados, 0);
    const donde = [
      conDirector > 0 ? `${conDirector} con tu director` : '',
      conRH > 0 ? `${conRH} con RH` : '',
    ]
      .filter(Boolean)
      .join(', ');
    const cuantas = sols.length === 1 ? '1 solicitud' : `${sols.length} solicitudes`;
    return `Tienes ${cuantas} en proceso (${dias} ${dias === 1 ? 'día' : 'días'}): ${donde}.`;
  });

  /** Cómo leer el saldo: deuda real, comprometido a futuro o disponible. Ver `leerSaldo`. */
  lecturaSaldo = computed(() => {
    const s = this.saldo();
    return s ? leerSaldo(s) : null;
  });

  /**
   * Por qué no se puede abrir una solicitud nueva, o `null` si se puede.
   *
   * Es la ÚNICA fuente de las dos entradas a la acción —el botón de la tarjeta de
   * saldo y el circular de la barra de la tabla—: deshabilitado y tooltip salen de
   * aquí para que no puedan divergir. Mientras carga, bloquea: el modal necesita el
   * saldo ya resuelto para validar los días.
   *
   * Tener solicitudes en proceso ya NO bloquea (vacaciones simultáneas).
   */
  readonly bloqueoNuevaSolicitud = computed((): string | null => {
    const s = this.saldo();
    if (!s) return 'Cargando saldo…';
    if (!s.desbloqueado) return 'Tus vacaciones se desbloquean a los 6 meses de antigüedad';
    if (this.loading()) return 'Cargando solicitudes…';
    return null;
  });

  /**
   * Solicitudes visibles en la tabla "Mis Solicitudes" según reglas de presentación:
   *
   * - Aprobada          → ocultar a partir del 2.º día después de la fecha fin
   *                        (ejemplo: fin=10 abr → visible el 11, oculto desde el 12)
   * - Rechazada /
   *   Rechazada Director /
   *   Cancelada          → ocultar después de 72 h desde el último cambio de estatus
   * - Pendiente /
   *   Aprobada Director  → siempre visibles
   */
  solicitudesVisibles = computed(() => {
    const ahora = new Date();

    return this.solicitudes().filter((sol) => {
      if (sol.estatus === 'Aprobada') {
        // sol.fechaFin puede venir como "2026-05-11" (mocks viejos) o como
        // timestamp ISO completo "2026-05-11T00:00:00" desde el backend.
        // new Date(...) directo maneja ambos; antes concatenábamos
        // 'T00:00:00' y eso producía Invalid Date con el formato nuevo,
        // ocultando todas las Aprobadas de la tabla.
        const fechaFin = new Date(sol.fechaFin);
        if (isNaN(fechaFin.getTime())) return true; // fail-open
        const limite = new Date(fechaFin);
        limite.setDate(limite.getDate() + 1);
        const hoy = new Date(ahora);
        hoy.setHours(0, 0, 0, 0);
        return hoy <= limite;
      }

      if (
        sol.estatus === 'Rechazada' ||
        sol.estatus === 'Rechazada Director' ||
        sol.estatus === 'Cancelada'
      ) {
        // `fechaActualizacion` encabezaba esta cadena, pero el backend NUNCA la
        // envió: el tipo a mano la declaraba y el contrato no la tiene, así que
        // la cadena siempre empezaba aquí. Quitarla no cambia el resultado.
        const tsStr = sol.fechaAprobacionRH ?? sol.fechaAprobacionDirector ?? sol.fechaSolicitud;
        const ts = new Date(tsStr);
        const horasDesde = (ahora.getTime() - ts.getTime()) / (1000 * 60 * 60);
        return horasDesde <= 72;
      }

      // Pendiente, Aprobada Director → siempre visible
      return true;
    });
  });

  ngOnInit(): void {
    const anioActual = new Date().getFullYear();
    this.festivosService.cargar([anioActual, anioActual + 1]);

    // La sesión YA está resuelta: `proveerSesion` la carga antes de montar el
    // primer componente. Aquí había un «camino feliz» con lectura síncrona y un
    // fallback con polling por si el bootstrap aún no había poblado el servicio;
    // los dos sobran, y con ellos las 330 líneas que los sostenían.
    this.aplicarSesion();
  }

  /**
   * Bifurca el flujo según rol:
   * - Admin / RH → getAll() para poder dar de alta solicitudes a nombre de
   *   cualquier empleado (uso administrativo).
   * - Director / Empleado / rol no mapeado → getMe() para autoseleccionarse;
   *   el selector queda oculto.
   */
  private aplicarSesion(): void {
    if (this.puedeSeleccionarOtro()) {
      // Admin/RH: cargar lista completa para el selector administrativo,
      // pero autoseleccionarse a sí mismos como default — así la pantalla
      // muestra su propio saldo al entrar, sin tener que buscar su nombre.
      // Si quieren revisar a otro empleado, lo cambian con el dropdown.
      this.empleadoService
        .getAll()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (data) => this.empleados.set(data.filter((e) => e.activo)),
        });

      this.empleadoService
        .getMe()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (emp) => {
            // Solo autoseleccionar si el usuario aún no ha elegido nada
            // manualmente (evita pisar la selección si getAll responde
            // primero y el usuario alcanzó a tocar el dropdown).
            if (emp?.id && this.selectedEmpleadoId() === null) {
              this.selectedEmpleadoId.set(emp.id);
              this.cargarDatos(emp.id);
            }
          },
          // Sin /me (cuenta sin perfil de empleado, ej. "admin admin"):
          // el selector queda vacío y el usuario elige manualmente.
          error: () => {
            /* silencioso — el selector queda disponible */
          },
        });
      return;
    }

    // Empleado / Director: autoselección vía /api/Empleado/me.
    this.empleadoService
      .getMe()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (emp) => {
          if (emp?.id) {
            this.miIdInterno.set(emp.idInterno ?? null);
            this.selectedEmpleadoId.set(emp.id);
            this.cargarDatos(emp.id);
          }
        },
        error: () => {
          // Sin perfil de empleado vinculado: dejar la pantalla limpia y avisar.
          this.message.error(
            'Tu cuenta no está vinculada a un perfil de empleado en RH. ' +
              'Contacta a sistemas para que se complete tu alta.',
            { nzDuration: 8000 },
          );
        },
      });
  }

  onEmpleadoChange(id: number): void {
    this.selectedEmpleadoId.set(id);
    if (id) {
      this.cargarDatos(id);
    } else {
      this.saldo.set(null);
      this.solicitudes.set([]);
    }
  }

  /**
   * Recarga del botón "Refrescar", que no existía en esta pantalla aunque es la
   * que más se queda abierta: el empleado la deja mientras espera que su
   * director resuelva, y no había forma de ver el cambio de estatus sin navegar
   * fuera y volver. Recarga saldo y solicitudes del empleado en pantalla, sea el
   * propio o el elegido en el selector administrativo.
   */
  recargar(): void {
    const id = this.selectedEmpleadoId();
    if (id !== null) this.cargarDatos(id);
  }

  cargarDatos(empleadoId: number): void {
    this.loading.set(true);
    this.vacacionesService
      .getSaldo(empleadoId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (s) => this.saldo.set(s),
      });
    this.vacacionesService
      .getByEmpleado(empleadoId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.solicitudes.set(
            data
              .slice()
              .sort(
                (a, b) =>
                  new Date(b.fechaSolicitud).getTime() - new Date(a.fechaSolicitud).getTime(),
              ),
          );
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  abrirNuevaSolicitud(): void {
    if (this.bloqueoNuevaSolicitud() !== null) return;
    this.nuevaModalVisible.set(true);
  }

  onNuevaGuardada(): void {
    const empId = this.selectedEmpleadoId();
    if (empId) this.cargarDatos(empId);
  }

  abrirEditar(sol: SolicitudVacaciones): void {
    this.editSolicitud.set(sol);
    this.editModalVisible.set(true);
  }

  onEditarGuardado(): void {
    const empId = this.selectedEmpleadoId();
    if (empId) this.cargarDatos(empId);
  }

  async confirmarCancelar(sol: SolicitudVacaciones): Promise<void> {
    const result = await Swal.fire({
      title: '¿Cancelar esta solicitud?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, cancelar',
      cancelButtonText: 'No',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'confirm-btnSweet',
        cancelButton: 'cancel-btnSweet',
        actions: 'swal-actions-spaced',
      },
    });
    if (result.isConfirmed) {
      this.cancelarSolicitud(sol.id);
    }
  }

  cancelarSolicitud(id: number): void {
    const empId = this.selectedEmpleadoId();
    this.vacacionesService
      .cancelar(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.message.success('Solicitud cancelada');
          if (empId) this.cargarDatos(empId);
        },
        error: () => this.message.error('Error al cancelar'),
      });
  }

  abrirResolucion(sol: SolicitudVacaciones): void {
    this.resolSolicitud.set(sol);
    this.resolVisible.set(true);
  }

  getEstatusColor = _getEstatusColor;

  puedeCancelar(estatus: EstatusVacaciones): boolean {
    return estatus === 'Pendiente' || estatus === 'Aprobada Director';
  }
}
